import React, { useState } from 'react';
import { AssetActionButtons, Chip, Thumb } from './FourAssetCard';
import { cardActionsOf, cardNoteOf, imageChipOf, kycChipOf, sourceChipOf, type AssetCard } from './workflowFourAssetsUtils';
import { otherLooksLabelOf, sceneUsageLineOf, videoLineOf, type AssetGroupView, type AssetRow } from './workflowFourAssetGroupsUtils';

interface RowProps {
  row: AssetRow;
  runStep?: 'ASSETS' | 'BOARD' | null;
  /** nhân vật gốc của thẻ: ảnh các bộ đồ của CÙNG nhân vật là một nhóm xem lớn */
  characterId: string;
  /** bộ không dùng trong plan này (nằm trong «Bộ đồ khác»): mờ hơn */
  other?: boolean;
  readOnly: boolean;
  busy: boolean;
  onEdit: (card: AssetCard) => void;
  onRegen: (card: AssetCard) => void;
  onUpload: (card: AssetCard, file: File) => void;
  onKyc: (card: AssetCard) => void;
}
/** Một hàng = (hình thái/tuổi × bộ đồ): ảnh nhỏ, nhãn «6 tuổi · bộ chính», chip, video dùng (gấp khi > 4), nút theo trạng thái. */
const CharacterRow: React.FC<RowProps> = ({ row, characterId, other, readOnly, runStep, busy, onEdit, onRegen, onUpload, onKyc }) => {
  const { card } = row;
  const [expanded, setExpanded] = useState(false);
  const actions = cardActionsOf(card, readOnly || busy, runStep ?? null);
  const note = cardNoteOf(card);
  const kycChip = kycChipOf(card);
  const sourceChip = sourceChipOf(card);
  const imageChip = card.status === 'READY' ? null : imageChipOf(card.status);
  const readyChip = card.status === 'READY' ? imageChipOf('READY') : null;
  const failed = card.status === 'FAILED' || card.status === 'BLOCKED' || card.status === 'UNCERTAIN' || (!!card.kyc?.required && (card.kyc.status === 'FAILED' || card.kyc.status === 'UNKNOWN'));
  const emphasis = row.flags.some((f) => f.regen);
  const videos = videoLineOf(row.videos, expanded);
  const sceneLine = sceneUsageLineOf(row);
  return <div className={`wf4a-row ${failed || row.flags.some((f) => f.tone === 'warn') ? 'bad' : ''} ${card.isMain && !other ? 'main' : ''} ${other ? 'other' : ''}`} data-testid={`asset-card-${card.key}`} data-status={card.status}>
    <Thumb url={card.imageUrl} kind={card.kind} status={card.status} name={`${row.label}`} spinning={busy} group={`char:${characterId}`} />
    <div className="wf4a-row-body">
      <b className="wf4a-row-label" title={row.label} data-testid={`row-label-${card.key}`}>{row.label}</b>
      {sceneLine && <small className="wf4a-row-scenes" title={sceneLine} data-testid={`row-scenes-${card.key}`}>{sceneLine}</small>}
      <div className="wf4a-chips"><Chip chip={readyChip} /><Chip chip={imageChip} /><Chip chip={kycChip} /><Chip chip={sourceChip} /></div>
      {note && <p className={`wf4a-note ${note.tone}`} title={note.text}>{note.text}</p>}
      {row.flags.filter((f) => f.tone === 'warn').map((flag) => <p key={flag.code} className="wf4a-note warn" title={flag.text} data-testid={`row-flag-${flag.code}-${card.key}`}>{flag.text}</p>)}
      {row.flags.filter((f) => f.tone === 'info').map((flag) => <small key={flag.code} className="wf4a-faint wf4a-row-dim" data-testid={`row-flag-${flag.code}-${card.key}`}>{flag.text}</small>)}
      {row.notes.map((text) => <small key={text} className="wf4a-faint wf4a-row-dim">{text}</small>)}
      <small className="wf4a-faint wf4a-row-videos" data-testid={`row-videos-${card.key}`}>
        {videos.text}
        {videos.foldable && <> <button type="button" className="wf4a-linkbtn" onClick={() => setExpanded((v) => !v)}>{expanded ? 'thu gọn' : 'xem hết'}</button></>}
      </small>
      <AssetActionButtons card={card} actions={actions} busy={busy} onEdit={onEdit} onRegen={onRegen} onUpload={onUpload} onKyc={onKyc} regenEmphasis={emphasis} />
      {!actions.edit && actions.disabledReason && card.status !== 'RUNNING' && <small className="wf4a-faint">{actions.disabledReason}</small>}
    </div>
  </div>;
};

interface GroupProps extends Omit<RowProps, 'row' | 'busy' | 'characterId' | 'other'> { group: AssetGroupView; /** `key` các thẻ đang có thao tác. */ busyKeys: Set<string> }
/** Thẻ MỘT nhân vật (người hoặc con vật) — đầu thẻ: ảnh, tên, chip «Con vật»; thân: các hàng hình thái × bộ đồ. */
export const FourCharacterGroupCard: React.FC<GroupProps> = ({ group, busyKeys, ...rest }) => {
  const [otherOpen, setOtherOpen] = useState(false);
  const rowProps = (row: AssetRow, other?: boolean) => ({ row, other, characterId: group.characterId, busy: busyKeys.has(row.card.key), ...rest });
  return <article className={`wf4a-group ${group.attention === 'bad' ? 'bad' : ''}`} data-testid={`asset-group-${group.characterId}`}>
    <header className="wf4a-group-head">
      <b className="wf4a-name" title={group.name}>{group.name}</b>
      {group.kind === 'ANIMAL' && <span className="wf4a-chip idle" data-testid={`group-animal-${group.characterId}`}>Con vật{group.species ? ` · ${group.species}` : ''}</span>}
      <span className="wf4a-faint">{group.ready}/{group.total} ảnh xong</span>
    </header>
    {/* Bộ đồ DÙNG trong plan: một dải ngang cuộn được, thẻ không dài ra khi nhân vật có nhiều bộ. */}
    <div className="wf4a-group-rows" data-testid={`group-rows-${group.characterId}`}>
      {group.mainRows.map((row) => <CharacterRow key={row.key} {...rowProps(row)} />)}
    </div>
    {group.otherRows.length > 0 && <div className="wf4a-others">
      <button type="button" className="wf4a-others-toggle" aria-expanded={otherOpen} data-testid={`group-others-${group.characterId}`} onClick={() => setOtherOpen((v) => !v)}>
        {otherOpen ? '▾' : '▸'} {otherLooksLabelOf(group.otherRows.length)}<small> — không dùng trong plan này</small>
      </button>
      {otherOpen && <div className="wf4a-group-rows other" data-testid={`group-others-rows-${group.characterId}`}>{group.otherRows.map((row) => <CharacterRow key={row.key} {...rowProps(row, true)} />)}</div>}
    </div>}
  </article>;
};
